import React, { useEffect, useState } from 'react';
import { Auth0Provider } from '@auth0/auth0-react';
import { useSecureConfigContext } from '../SecureConfig/SecureConfigProvider';

const Auth0ProviderWrapper = ({ children }) => {
    const { getAuthDomain, getAuthClient, hasSecureConfig, loading, error, refetch } = useSecureConfigContext();
    const [retryCount, setRetryCount] = useState(0);
    const [retriesExhausted, setRetriesExhausted] = useState(false);

    // Handle error with auto-retry (no annoying alerts)
    useEffect(() => {
        if (error) {
            console.warn('Failed to load secure authentication configuration:', error);

            // Check if we've already tried multiple times
            if (retryCount < 2) {
                // Allow 2 component-level retries
                const retryTimer = setTimeout(() => {
                    setRetryCount((prev) => prev + 1);
                    refetch();
                }, 2000);

                return () => clearTimeout(retryTimer);
            } else {
                // All retries exhausted
                setRetriesExhausted(true);
            }
        } else {
            // Reset retry state on successful config load
            setRetryCount(0);
            setRetriesExhausted(false);
        }
    }, [error, refetch, retryCount]);

    // Show loading state during config fetch
    if (loading) {
        return (
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '100vh',
                    fontSize: '16px',
                }}
            >
                Loading secure configuration...
            </div>
        );
    }

    // Show error state if config fetch failed and retries are happening
    if (error || !hasSecureConfig()) {
        if (retriesExhausted) {
            return (
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: '100vh',
                        fontSize: '16px',
                        color: '#d32f2f',
                        textAlign: 'center',
                    }}
                >
                    <div style={{ marginBottom: '20px' }}>Unable to connect to authentication service.</div>
                    <button
                        onClick={() => window.location.reload()}
                        style={{
                            padding: '10px 20px',
                            fontSize: '14px',
                            backgroundColor: '#1976d2',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                        }}
                    >
                        Refresh Page
                    </button>
                </div>
            );
        }

        return (
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '100vh',
                    fontSize: '16px',
                    color: '#f57c00',
                }}
            >
                Connecting to authentication service... (Attempt {retryCount + 1})
            </div>
        );
    }

    const domain = getAuthDomain();
    const clientId = getAuthClient();

    // Ensure we have valid auth credentials from secure config
    if (!domain || !clientId) {
        return (
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '100vh',
                    fontSize: '16px',
                    color: '#d32f2f',
                }}
            >
                Authentication not properly configured.
            </div>
        );
    }

    return (
        <Auth0Provider
            domain={domain}
            clientId={clientId}
            authorizationParams={{
                redirect_uri: window.location.origin,
            }}
        >
            {children}
        </Auth0Provider>
    );
};

export default Auth0ProviderWrapper;
